import { html, unsafeCSS } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import debounce from 'lodash.debounce';

import { PopBaseComponent } from '../../../base/pop-base-component';
import { Constants } from './product-center-search-bar.constants';

import style from './product-center-search-bar.scss?inline';
import { IconSVG } from '../../../common/utils/icon';
import { FundsSearchService } from '../../../common/fuse/fuseSearch';
import type { IProductCenterStore } from '../../store/product-center.store.model';
import productCenterStore, {
  zustandStateToComponent,
} from '../../store/product-center.store';
import { observeAndSetBeaconPopoverWidth } from '../../product-center-table-wrapper/product-center-table-wrapper.utils';

/**
 * @tag pop-pc-search-bar
 * @property {tableContent} [componentObject] content from the table
 * @attribute {tableContent} [data-component-object] content from the table
 */

export class PopSearchBarComponent extends PopBaseComponent {
  /**
   * Theme details
   */
  @property({
    attribute: 'data-theme',
    type: String,
  })
  protected theme?: string;

  @state()
  productCenterState: IProductCenterStore = productCenterStore.getState();

  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }
  static readonly tagName = Constants.TAG_NAME;

  @property()
  searchBarInputValue = '';

  @state()
  private isPopoverOpen = false;

  @query('#pop-search-field-popover')
  beaconPopover!: HTMLElement;

  @query('#pop-search-field-container')
  inputSearchField!: HTMLElement;

  private readonly fundSearchService: FundsSearchService =
    new FundsSearchService();

  constructor() {
    super();
    this.enableI18n = true;
  }

  /*
   * Component Methods
   */

  firstUpdated() {
    this.initializeFuse();
  }

  update(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('isPopoverOpen')) {
      if (this.beaconPopover && this.inputSearchField) {
        observeAndSetBeaconPopoverWidth(
          this.beaconPopover,
          this.inputSearchField,
        );
      }
    }
    super.update(changedProperties);
  }

  private initializeFuse() {
    this.fundSearchService.initFuse(
      productCenterStore.getState().originalProductCenterObject.products,
    );
  }

  /**
   * Bolds the matched characters in a given text based on the search term.
   * This method highlights the characters in the text that match the search term
   * by wrapping them in <b> tags.
   *
   * @param {string} text - The text in which to bold the matched characters.
   * @param {string} toMatch - The search term to match and bold in the text.
   * @returns {string} - The text with matched characters wrapped in <b> tags.
   *
   * @example
   * const result = boldMatchCharacters('Concentrated Global Equity Fund', 'Global');
   */
  boldMatchCharacters(text: string, toMatch: string) {
    const regex = new RegExp(toMatch, 'gi'); // 'g' for global, 'i' for case-insensitive
    if (!regex.test(text)) {
      return `${text}`;
    } else {
      return text.replace(regex, (match) => `<b>${match}</b>`);
    }
  }

  handleResultClick(matchValue: string) {
    this.fundSearchService.searchFunds(matchValue);
    this.productCenterState.setSearchTerm(matchValue);
    this.isPopoverOpen = false;
  }

  handleResultKeyDown(event: KeyboardEvent, matchValue: string) {
    if (
      event.code === Constants.EVENTS.KEYDOWN.SPACE ||
      event.code === Constants.EVENTS.KEYDOWN.ENTER
    ) {
      event.preventDefault();
      this.handleResultClick(matchValue);
    }
  }

  private handleSearch() {
    this.fundSearchService.matchesFromSearch = [];
    if (
      this.searchBarInputValue &&
      this.searchBarInputValue.length >= Constants.FUSE.MIN_LENGTH
    ) {
      const searchTerm = this.searchBarInputValue;

      this.fundSearchService.searchFunds(searchTerm);
      this.productCenterState.setSearchTerm(searchTerm);
      this.isPopoverOpen = this.fundSearchService.matchesFromSearch.length
        ? true
        : false;
    } else {
      this.productCenterState.setSearchTerm(this.searchBarInputValue, {
        searchAllProducts: true,
      });
      this.fundSearchService.matchesFromSearch = [];
      this.isPopoverOpen = false;
    }

    const arrowElement = this.beaconPopover.shadowRoot?.querySelector(
      '.arrow',
    ) as HTMLElement;
    if (arrowElement) {
      arrowElement.style.display = 'none';
    }
    this.updateErrorMessage();
    this.requestUpdate();
  }

  updateErrorMessage() {
    const beaconControl = this.shadowRoot?.querySelector('beacon-control');
    if (
      this.fundSearchService.matchesFromSearch.length ||
      this.searchBarInputValue.length < Constants.SEARCH_MIN_LENGTH
    ) {
      beaconControl?.removeAttribute('invalid');
      beaconControl?.querySelector('input')?.removeAttribute('aria-invalid');
    } else {
      beaconControl?.setAttribute('invalid', '');
      beaconControl
        ?.querySelector('input')
        ?.setAttribute('aria-invalid', 'true');
    }
  }

  clearSearchBarInput() {
    this.searchBarInputValue = '';
    this.productCenterState.setSearchTerm(this.searchBarInputValue, {
      searchAllProducts: true,
    });
    this.fundSearchService.matchesFromSearch = [];
    this.isPopoverOpen = false;
    this.updateErrorMessage();
    this.inputSearchField.querySelector('input')?.focus();
  }

  private readonly debouncedSearchBarInputListener = debounce(() => {
    this.handleSearch();
  }, 300);

  dispatchInputValue(e: Event) {
    const inputElement = e.target as HTMLInputElement;
    this.searchBarInputValue = inputElement.value;
    this.debouncedSearchBarInputListener();
  }

  private renderSearchField() {
    return html` <div class="pop-search-bar-wrapper">
      <div id="pop-search-field-container" class="pop-search-field">
        <beacon-control>
          <label slot="label" for="pop-search-field"
            >${this.localize(Constants.TRANSLATION_KEYS.SEARCH)}</label
          >
          <input
            type="text"
            name="pop-search-field"
            id="pop-search-field-trigger"
            aria-invalid="false"
            aria-disabled="false"
            aria-label="${this.localize(Constants.TRANSLATION_KEYS.SEARCH)}"
            placeholder="${this.localize(
              Constants.TRANSLATION_KEYS.SEARCHBAR_PLACEHOLDER_TEXT,
            )}"
            .value=${this.searchBarInputValue}
            @input=${this.dispatchInputValue}
            autocomplete="off"
          />
          <span slot="error"
            >${this.localize(Constants.TRANSLATION_KEYS.ERROR_MESSAGE)}</span
          >
          <div slot="hint">
            ${this.localize(Constants.TRANSLATION_KEYS.SEARCH_PLACEHOLDER)}
          </div>
          <div slot="suffix" class="pop-search-icon">
            ${this.renderSearchFieldIcon()}
          </div>
          <beacon-popover
            id="pop-search-field-popover"
            class="pop-product-center-popover"
            ?open="${this.isPopoverOpen}"
            opens-by="#pop-search-field-trigger"
            placement="bottom"
            style="${!this.isPopoverOpen ? 'display: none;' : ''}"
          >
            <beacon-menu>
              <ul class="pop-search-results-list" id="pop-search-results-list">
                ${this.fundSearchService.matchesFromSearch.map(
                  (match) => html`
                    <li
                      tabindex="0"
                      class="${Constants.ATTRIBUTES.SEARCH_RESULT_NAME}"
                      @keydown="${(event) =>
                        this.handleResultKeyDown(event, match.value ?? '')}"
                      @click="${() =>
                        this.handleResultClick(match.value ?? '')}"
                    >
                      ${unsafeHTML(
                        this.boldMatchCharacters(
                          match.value ?? '',
                          this.searchBarInputValue,
                        ),
                      )}
                    </li>
                  `,
                )}
              </ul>
            </beacon-menu>
          </beacon-popover>
        </beacon-control>
      </div>
    </div>`;
  }

  renderSearchFieldIcon() {
    if (!this.searchBarInputValue.length) {
      return html` ${IconSVG.magnify({ slot: Constants.ICON_SLOT })} `;
    } else {
      return html`
        <beacon-icon-button
          class="close-button"
          variant="tertiary"
          scale="mini"
          tabindex="0"
          @beacon-event-click="${this.clearSearchBarInput}"
        >
          ${IconSVG.close({
            slot: Constants.ICON_SLOT,
            ariaLabel: this.localize(Constants.TRANSLATION_KEYS.CLEAR_SEARCH),
          })}
        </beacon-icon-button>
      `;
    }
  }

  renderComponent() {
    const selectedTheme = ['dark', 'darker'].includes(this.theme as string);
    return html` <div
      class="pop-search-bar-box ${selectedTheme ? 'noBackground' : ''}"
    >
      ${this.renderSearchField()}
    </div>`;
  }

  render() {
    return this.renderComponent();
  }
}

customElements.define(
  Constants.TAG_NAME,
  zustandStateToComponent(
    {
      mapState: (productCenterState) => ({
        searchBarInputValue: productCenterState.searchTerm,
        originalProductCenterObject:
          productCenterState.originalProductCenterObject,
        filteredProducts: productCenterState.getFilteredProducts(),
      }),
    },
    PopSearchBarComponent,
  ),
);
